import { useEffect, useState } from "react";
import { apiGet, apiSend } from "@/lib/api";
import type { Scene } from "@/mocks/scenes";
import type { Device } from "@/mocks/devices";
import { usePersonas } from "@/hooks/usePersonas";

// Create / edit a scene (trigger point). Present `scene` = edit mode.
// `afterCreate` 仅在新增模式生效：POST 成功拿到完整 scene 后会先 await
// afterCreate(scene)，再 onSaved + onClose —— 用于游线"添加点位"这类
// 新建后还要把 scene 关联到其他实体的场景，避免调用方再做一次 GET。
function SceneFormModal({
  scene,
  groups = [],
  defaultGroupId = "",
  onClose,
  onSaved,
  afterCreate,
  title,
  submitLabel,
}: {
  scene?: Scene;
  groups?: { id: string; name: string }[];
  defaultGroupId?: string;
  onClose: () => void;
  onSaved: () => void;
  afterCreate?: (scene: Scene) => Promise<void> | void;
  title?: string;
  submitLabel?: string;
}) {
  const { names: VOICES } = usePersonas();
  const isEdit = !!scene;
  const [form, setForm] = useState({
    name: scene?.name ?? "",
    description: scene?.description ?? "",
    lat: scene?.lat ?? 30.82,
    lng: scene?.lng ?? 111.0,
    triggerRadius: scene?.triggerRadius ?? 300,
    visibility: scene?.visibility ?? "public",
    voice: scene?.voice ?? (VOICES[0] ?? ""),
    status: scene?.status ?? "active",
    groupId: scene?.groupId ?? defaultGroupId,
    isStandard: scene?.isStandard ?? false,
    triggerMode: scene?.triggerMode ?? "circle",
    triggerHeadingDeg: scene?.triggerHeadingDeg ?? "",
    triggerWidthM: scene?.triggerWidthM ?? "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // 从设备实采坐标：列出有上报位置的设备，选中即回填 lat/lng（WGS-84 原始坐标）。
  const [posDevices, setPosDevices] = useState<Device[]>([]);
  useEffect(() => {
    apiGet<Device[]>("/devices")
      .then((ds) => setPosDevices((ds ?? []).filter((d) => d.lastLat && d.lastLng)))
      .catch(() => {});
  }, []);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      setError("请填写点位名称");
      return;
    }
    if (form.triggerMode === "strip") {
      const heading = form.triggerHeadingDeg !== "" && form.triggerHeadingDeg !== null ? Number(form.triggerHeadingDeg) : NaN;
      const width = form.triggerWidthM !== "" && form.triggerWidthM !== null ? Number(form.triggerWidthM) : NaN;
      if (!Number.isFinite(heading)) {
        setError("条带模式朝向角必须为有效数字");
        return;
      }
      if (!Number.isFinite(width) || width <= 0) {
        setError("条带模式宽度必须为大于 0 的数字");
        return;
      }
    }
    setSubmitting(true);
    setError("");
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        lat: Number(form.lat),
        lng: Number(form.lng),
        triggerRadius: Number(form.triggerRadius) || 300,
        visibility: form.visibility,
        voice: form.voice,
        status: form.status,
        groupId: form.groupId,
        isStandard: form.isStandard,
        // PUT replaces all columns — carry derived counters and basic-info
        // extras (edited in the drawer) through unchanged.
        audioStatus: scene?.audioStatus ?? "none",
        knowledgeCount: scene?.knowledgeCount ?? 0,
        imageCount: scene?.imageCount ?? 0,
        recognitionHint: scene?.recognitionHint ?? "",
        coverUrl: scene?.coverUrl ?? "",
        tags: scene?.tags ?? [],
        triggerMode: form.triggerMode,
        triggerHeadingDeg: form.triggerMode === "strip" && form.triggerHeadingDeg !== "" && form.triggerHeadingDeg !== null ? Number(form.triggerHeadingDeg) : null,
        triggerWidthM: form.triggerMode === "strip" && form.triggerWidthM !== "" && form.triggerWidthM !== null ? Number(form.triggerWidthM) : null,
      };
      if (isEdit) {
        await apiSend("PUT", `/scenes/${scene.id}`, body);
      } else {
        const created = await apiSend<Scene>("POST", "/scenes", body);
        if (afterCreate) await afterCreate(created);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const input =
    "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title ?? (isEdit ? `编辑点位 ${scene.name}` : "新增点位")}</h2>
            <p className="text-xs text-gray-500 mt-0.5">讲解触发点的位置、半径与基础信息</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">点位名称 *</label>
            <input className={input} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="如 三峡大坝" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">简介</label>
            <textarea
              className={`${input} h-20 resize-none`}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="一句话介绍，会用于讲解兜底文案"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">纬度 lat（WGS-84）</label>
              <input className={input} type="number" step="0.0001" value={form.lat} onChange={(e) => set("lat", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">经度 lng（WGS-84）</label>
              <input className={input} type="number" step="0.0001" value={form.lng} onChange={(e) => set("lng", e.target.value)} />
            </div>
            {posDevices.length > 0 && (
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  从设备取位 <span className="text-gray-400">（取该设备最近一次 GPS 上报）</span>
                </label>
                <select
                  className={input}
                  defaultValue=""
                  onChange={(e) => {
                    const d = posDevices.find((x) => x.id === e.target.value);
                    if (d?.lastLat && d?.lastLng) {
                      setForm((f) => ({ ...f, lat: d.lastLat!, lng: d.lastLng! }));
                    }
                  }}
                >
                  <option value="">选择设备…</option>
                  {posDevices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.deviceNo}（{d.lastLat!.toFixed(5)}, {d.lastLng!.toFixed(5)}，{d.lastOnline}）
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">触发半径（米）</label>
              <input className={input} type="number" min={50} step={50} value={form.triggerRadius} onChange={(e) => set("triggerRadius", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">触发模式</label>
              <select className={input} value={form.triggerMode} onChange={(e) => set("triggerMode", e.target.value)}>
                <option value="circle">圆形 (Circle)</option>
                <option value="strip">条带 (Strip)</option>
              </select>
            </div>
            {form.triggerMode === "strip" && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">朝向角（度）</label>
                  <input
                    className={input}
                    type="number"
                    step="0.01"
                    value={form.triggerHeadingDeg ?? ""}
                    onChange={(e) => set("triggerHeadingDeg", e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="例如 90"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">bearing 0° north / 90° east</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">容忍宽度（米，总宽）</label>
                  <input
                    className={input}
                    type="number"
                    min={0}
                    step={10}
                    value={form.triggerWidthM ?? ""}
                    onChange={(e) => set("triggerWidthM", e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="例如 100"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">条带总宽，实际左右各取一半</p>
                </div>
              </>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">默认讲解音色</label>
              <select className={input} value={form.voice} onChange={(e) => set("voice", e.target.value)}>
                {VOICES.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">分组</label>
              <select className={input} value={form.groupId} onChange={(e) => set("groupId", e.target.value)}>
                <option value="">未分组</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-start gap-2 p-3 bg-teal-50/60 border border-teal-200 rounded-lg cursor-pointer hover:bg-teal-50">
            <input
              type="checkbox"
              checked={form.isStandard}
              onChange={(e) => setForm((f) => ({ ...f, isStandard: e.target.checked }))}
              className="mt-0.5 accent-teal-600"
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900">归入标准点位（公共可复用）</div>
              <div className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                勾选后该点位会出现在「点位内容」列表（按上行/下行分组），其它游线可通过「从点位库导入」复用。
                不勾选则仅本游线私有，列表不展示。
              </div>
            </div>
          </label>

          {error && (
            <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {submitting ? "保存中..." : (submitLabel ?? (isEdit ? "保存修改" : "确认新增"))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SceneFormModal;
